<h2 class="section-title">
  {{
    (userView() ? 'USER.TRAVEL_REQUESTS.TABLE.TITLE' : 'USER.TRAVEL_REQUESTS.TABLE.TITLE_ADMIN')
      | translate
  }}
</h2>

<form>
  @if (!userView()) {
    <mat-form-field class="filter" color="primary" appearance="fill">
      <mat-label>
        {{ 'USER.TRAVEL_REQUESTS.FILTER_EMPLOYEE' | translate }}
      </mat-label>
      <mat-chip-grid aria-label="Filter by employees" #employeeList [formControl]="employeeCtrl">
        @for (employee of selectedEmployees; track employee) {
          <mat-chip-row color="primary" [removable]="true" (removed)="removeEmployee(employee.id)">
            {{ employee.firstNameEn + ' ' + employee.lastNameEn }}
            <mat-icon matChipRemove>cancel</mat-icon>
          </mat-chip-row>
        }
        <input
          #employeesInput
          matInput
          [matAutocomplete]="auto"
          [formControl]="employeeCtrl"
          [matChipInputFor]="employeeList"
          [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
          (matChipInputTokenEnd)="onAddEmployee($event)"
          (focusout)="clearParticipantInputValue()"
        />
      </mat-chip-grid>
      <mat-autocomplete
        #auto="matAutocomplete"
        (optionSelected)="autocompleteSelect($event)"
        [attr.disabled]="true"
      >
        @for (employee of filteredEmployees | async; track employee) {
          <mat-option [value]="employee">
            {{ employee.firstNameEn + ' ' + employee.lastNameEn }}
          </mat-option>
        }
      </mat-autocomplete>
    </mat-form-field>
  }
</form>

<div class="table-holder">
  @if (isLoading) {
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  }
  <table
    mat-table
    [dataSource]="dataSource"
    matSort
    matSortActive="id"
    matSortDirection="asc"
    (matSortChange)="sortData($event)"
  >
    <div class="highlight">
      <ng-container matColumnDef="request-id">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'USER.TRAVEL_REQUESTS.REQUEST_ID' | translate }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.businessTripId }}
        </td>
      </ng-container>
      <ng-container matColumnDef="requester">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'USER.TRAVEL_REQUESTS.REQUESTER' | translate }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.requester }}
        </td>
      </ng-container>
      <ng-container matColumnDef="participant">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'USER.TRAVEL_REQUESTS.PARTICIPANT' | translate }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ getParticipantNames(element) }}
        </td>
      </ng-container>
      <ng-container matColumnDef="departureDate">
        <th mat-header-cell mat-sort-header="departureDate" *matHeaderCellDef>
          {{ 'USER.TRAVEL_REQUESTS.DEPARTURE_DATE' | translate }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.departureDate }}
        </td>
      </ng-container>
      <ng-container matColumnDef="returnDate">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>
          {{ 'USER.TRAVEL_REQUESTS.RETURN_DATE' | translate }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.returnDate }}
        </td>
      </ng-container>
      <ng-container matColumnDef="start-destination">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'USER.TRAVEL_REQUESTS.START_DESTINATION' | translate }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.startDestination }}
        </td>
      </ng-container>
      <ng-container matColumnDef="end-destination">
        <th mat-header-cell *matHeaderCellDef>
          {{ 'USER.TRAVEL_REQUESTS.END_DESTINATION' | translate }}
        </th>
        <td mat-cell *matCellDef="let element">
          {{ element.endDestination }}
        </td>
      </ng-container>
      <ng-container matColumnDef="update-delete-icons">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let element" class="icons-holder">
          <div class="flex">
            @if (
              userView() &&
              (['viewUser.viewMyBusinessTravelRequests.manageMyBusinessTravelRequests']
                | hasPermission)
            ) {
              <mat-icon
                [class.hidden]="userId === element.requesterId || element.status === 'Completed'"
                matTooltip="{{ 'USER.TRAVEL_REQUESTS.UPLOAD_DOCUMENTS' | translate }}"
                (click)="openUploadDocumentsDialog(element)"
                color="primary"
                >cloud_upload
              </mat-icon>
            }
            @if (
              userView()
                ? (['viewUser.viewMyBusinessTravelRequests.manageMyBusinessTravelRequests']
                  | hasPermission)
                : (['viewAdmin.viewAdministration.viewTravelReport.editTravelRequest'] | hasPermission)
            ) {
              <mat-icon
                [class.disabled]="
                  (userId !== element.requesterId && this.userView()) ||
                  (element.status !== 'Created' && this.userView())
                "
                color="primary"
                (click)="editTravelRequest(element)"
                matTooltip="{{
                  (element.status === 'Created' || isAdmin
                    ? 'USER.TRAVEL_REQUESTS.EDIT'
                    : 'USER.TRAVEL_REQUESTS.VIEW'
                  ) | translate
                }}"
                class="edit-icon"
                >{{ element.status === 'Created' || isAdmin ? 'edit' : 'visibility' }}
              </mat-icon>
            }
            @if (
              userView()
                ? (['viewUser.viewMyBusinessTravelRequests.manageMyBusinessTravelRequests']
                  | hasPermission)
                : (['viewAdmin.viewAdministration.viewTravelReport.deleteTravelRequest'] | hasPermission)
            ) {
              <mat-icon
                [class.disabled]="
                  (this.userView() && element.status === 'Completed') ||
                  (userId !== element.requesterId && this.userView())
                "
                matTooltip="{{ 'USER.TRAVEL_REQUESTS.DELETE' | translate }}"
                (click)="openDialog(element)"
                class="delete-icon"
                >cancel
              </mat-icon>
            }
          </div>
        </td>
      </ng-container>
    </div>
    <tr mat-header-row *matHeaderRowDef="getDisplayedColumns()"></tr>

    <tr
      mat-row
      *matRowDef="let row; columns: getDisplayedColumns()"
      [ngClass]="row.businessTripId === currentTripId() ? 'highlight' : ''"
    ></tr>
    <tr class="mat-row" *matNoDataRow>
      <td class="mat-cell" colspan="10">
        {{ 'SHARED.COMPONENTS.TABLE_WRAPPER.NO_RECORDS_FOUND' | translate }}
      </td>
    </tr>
  </table>

  @if (!isDisabledPagination) {
    <mat-paginator
      class="result-table-paginator"
      [length]="dataLength"
      [pageSizeOptions]="[10, 20, 50]"
      showFirstLastButtons
      aria-label="Select page of periodic elements"
    >
    </mat-paginator>
  }
</div>
